// src/components/Menu/MenuManager.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Toolbar from "./Toolbar";
import Column from "./Column";
import CategoryList from "./CategoryList";
import SubcategoryList from "./SubcategoryList";
import ItemList from "./ItemList";
import "./MenuManager.css"
import GenericEditModal from "./GenericEditModal";
import EditMenuItemModal from "./EditMenuItemModal";
import AddMainCategoryModal from "./AddMainCategoryModal";
import AddSubcategoryModal from "./AddSubcategoryModal";
import UniversalSidesModal from "./UniversalSidesModal";
import AllergenManagerModal from "./AllergenManagerModal";
import * as XLSX from "xlsx";
import {
  fetchMenu,
  addMainCategory,
  editCategoryFields,
  editSubcategoryFields,
  editItemFields,
  addSubcategoryApi,
  createMenuItem,
  deleteMainCategory,
  deleteSubcategoryApi,
  deleteMenuItem,
  addSideMenuItem,
  fetchAllergens,
  addAllergen,
  dumpMenuSQL,
  clearMenu,
  resetMenuItemRatings,
} from "../../services/menuApi";

import Constants from "../Constants/Constants";
import { useAuth } from "../../auth/AuthContext";
import {
  downloadMenuImportTemplate,
  parseMenuImportFile,
  applyMenuImport,
  formatImportSummary,
  formatAllergenTitlesForExport,
} from "./menuImport";
import { buildMenuWorkbook, downloadMenuBackupZip } from "./menuBackup";

/* ---------------- Spinner components ---------------- */
const spinKeyframes = `
@keyframes tm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;
const styleTagId = "tm-spinner-kf";
if (typeof document !== "undefined" && !document.getElementById(styleTagId)) {
  const s = document.createElement("style");
  s.id = styleTagId;
  s.innerHTML = spinKeyframes;
  document.head.appendChild(s);
}

function Spinner({ size = 18, stroke = 3 }) {
  const dim = size;
  const s = {
    width: dim,
    height: dim,
    border: `${stroke}px solid rgba(0,0,0,0.15)`,
    borderTopColor: "red",
    borderRadius: "50%",
    animation: "tm-spin 0.8s linear infinite",
  };
  return <span style={s} aria-label="loading" />;
}

function SpinnerOverlay() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(255,255,255,0.6)",
        display: "grid",
        placeItems: "center",
        zIndex: 5,
      }}
    >
      <div style={{ display: "grid", gap: 10, placeItems: "center" }}>
        <Spinner size={34} stroke={4} />
        <div style={{ fontSize: 12, color: "#555" }}>Loading…</div>
      </div>
    </div>
  );
}
/* ---------------------------------------------------- */

function flattenMenuItems(menu) {
  const out = [];
  for (const cat of menu?.categories || []) {
    for (const sub of cat.subcategories || []) {
      for (const item of sub.items || []) {
        out.push({
          ...item,
          categoryId: cat.id,
          categoryName: cat.name,
          categoryCode: cat.code,
          subcategoryId: sub.id,
          subcategoryName: sub.name,
          subcategoryCode: sub.subcode,
          parentPath: `${cat.name} > ${sub.name}`,
        });
      }
    }
  }
  return out;
}

function itemSearchHaystack(it) {
  const allergenTitles = [
    ...(it.allergens_resolved || []).map((a) => a.title),
    ...(it.allergens?.titles || []),
  ];
  return [
    it.name,
    it.item,
    it.id,
    it.price,
    it.raw?.item,
    it.raw?.description,
    it.raw?.price,
    it.raw?.code,
    it.raw?.subcode,
    it.categoryName,
    it.subcategoryName,
    it.categoryCode,
    it.subcategoryCode,
    ...allergenTitles,
  ]
    .filter((v) => v !== undefined && v !== null && v !== "")
    .join(" ")
    .toLowerCase();
}

const CATEGORY_SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "name-asc", label: "Name A–Z" },
  { value: "name-desc", label: "Name Z–A" },
  { value: "code-asc", label: "Code" },
  { value: "visible-first", label: "Visible first" },
];

const SUBCATEGORY_SORT_OPTIONS = CATEGORY_SORT_OPTIONS;

const ITEM_SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "name-asc", label: "Name A–Z" },
  { value: "name-desc", label: "Name Z–A" },
  { value: "price-asc", label: "Price: low–high" },
  { value: "price-desc", label: "Price: high–low" },
  { value: "rating-desc", label: "Rating" },
  { value: "orders-desc", label: "Popularity" },
  { value: "visible-first", label: "Visible first" },
  { value: "path-asc", label: "Category" },
];

function cmpText(a, b) {
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function parseMenuNumber(v) {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function isMenuHidden(entity) {
  const vis =
    entity?.visibility ??
    entity?.visibilty ??
    entity?.raw?.visibility ??
    entity?.visible;
  return vis === 0 || vis === "0" || vis === false || vis === "False";
}

function sortMenuRows(list, mode, kind) {
  if (!Array.isArray(list) || list.length < 2 || !mode || mode === "default") {
    return list;
  }
  const next = [...list];
  next.sort((a, b) => {
    switch (mode) {
      case "name-asc":
        return cmpText(a.name, b.name) || cmpText(a.id, b.id);
      case "name-desc":
        return cmpText(b.name, a.name) || cmpText(a.id, b.id);
      case "code-asc":
        return cmpText(
          kind === "subcategory" ? a.subcode ?? a.id : a.code ?? a.id,
          kind === "subcategory" ? b.subcode ?? b.id : b.code ?? b.id
        );
      case "visible-first": {
        const diff = Number(isMenuHidden(a)) - Number(isMenuHidden(b));
        return diff || cmpText(a.name, b.name);
      }
      case "price-asc":
        return (
          parseMenuNumber(a.price ?? a.raw?.price) -
            parseMenuNumber(b.price ?? b.raw?.price) || cmpText(a.name, b.name)
        );
      case "price-desc":
        return (
          parseMenuNumber(b.price ?? b.raw?.price) -
            parseMenuNumber(a.price ?? a.raw?.price) || cmpText(a.name, b.name)
        );
      case "rating-desc":
        return (
          parseMenuNumber(b.raw?.rating ?? b.rating) -
            parseMenuNumber(a.raw?.rating ?? a.rating) ||
          cmpText(a.name, b.name)
        );
      case "orders-desc":
        return (
          parseMenuNumber(b.raw?.orders ?? b.orders) -
            parseMenuNumber(a.raw?.orders ?? a.orders) ||
          cmpText(a.name, b.name)
        );
      case "path-asc":
        return cmpText(a.parentPath, b.parentPath) || cmpText(a.name, b.name);
      default:
        return 0;
    }
  });
  return next;
}

const MenuManager = () => {
  const { tenantServerUrl } = useAuth();

  // Constants now built from tenantServerUrl (for experience list etc.)
  const constants = useMemo(
    () => new Constants(tenantServerUrl),
    [tenantServerUrl]
  );
  const EXPERIENCE_OPTIONS = useMemo(
    () => constants.getExperienceList(),
    [constants]
  );
  const [selectedItemId, setSelectedItemId] = useState(null);

  const [menu, setMenu] = useState({ categories: [] });
  const [loading, setLoading] = useState(false);
  const [addMainOpen, setAddMainOpen] = useState(false);
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [universalSidesOpen, setUniversalSidesOpen] = useState(false);
  const [allergensOpen, setAllergensOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  const [itemSearch, setItemSearch] = useState("");
  const [viewAllItems, setViewAllItems] = useState(false);
  const [itemsExpanded, setItemsExpanded] = useState(false);
  const [categorySort, setCategorySort] = useState("default");
  const [subcategorySort, setSubcategorySort] = useState("default");
  const [itemSort, setItemSort] = useState("default");

  const selectedCategory = useMemo(
    () => menu.categories.find((c) => c.id === selectedCategoryId) || null,
    [menu, selectedCategoryId]
  );

  const selectedSubcategory = useMemo(
    () =>
      selectedCategory?.subcategories?.find(
        (sc) => sc.id === selectedSubcategoryId
      ) || null,
    [selectedCategory, selectedSubcategoryId]
  );

  const menuTotals = useMemo(() => {
    const categories = menu.categories || [];
    let subcategories = 0;
    let items = 0;
    for (const cat of categories) {
      const subs = cat.subcategories || [];
      subcategories += subs.length;
      for (const sub of subs) {
        items += (sub.items || []).length;
      }
    }
    return {
      categories: categories.length,
      subcategories,
      items,
    };
  }, [menu]);

  const allMenuItems = useMemo(() => flattenMenuItems(menu), [menu]);

  const itemSearchQuery = itemSearch.trim().toLowerCase();
  const showingAllItems = viewAllItems || Boolean(itemSearchQuery);

  useEffect(() => {
    if (!showingAllItems) setItemsExpanded(false);
  }, [showingAllItems]);

  const displayedItems = useMemo(() => {
    const source = showingAllItems
      ? allMenuItems
      : (selectedSubcategory?.items || []).map((item) => ({
          ...item,
          categoryId: selectedCategory?.id,
          categoryName: selectedCategory?.name,
          categoryCode: selectedCategory?.code,
          subcategoryId: selectedSubcategory?.id,
          subcategoryName: selectedSubcategory?.name,
          subcategoryCode: selectedSubcategory?.subcode,
          parentPath:
            selectedCategory && selectedSubcategory
              ? `${selectedCategory.name} > ${selectedSubcategory.name}`
              : "",
        }));

    if (!itemSearchQuery) return source;
    return source.filter((it) =>
      itemSearchHaystack(it).includes(itemSearchQuery)
    );
  }, [
    showingAllItems,
    allMenuItems,
    selectedCategory,
    selectedSubcategory,
    itemSearchQuery,
  ]);

  const sortedCategories = useMemo(
    () => sortMenuRows(menu.categories || [], categorySort, "category"),
    [menu.categories, categorySort]
  );

  const sortedSubcategories = useMemo(
    () =>
      sortMenuRows(
        selectedCategory?.subcategories || [],
        subcategorySort,
        "subcategory"
      ),
    [selectedCategory, subcategorySort]
  );

  const sortedDisplayedItems = useMemo(
    () => sortMenuRows(displayedItems, itemSort, "item"),
    [displayedItems, itemSort]
  );

  // ---- Load / Refresh ----
  const load = async () => {
    if (!tenantServerUrl) {
      console.warn("[MenuManager] No tenantServerUrl available yet");
      return;
    }

    setLoading(true);
    try {
      const m = await fetchMenu(tenantServerUrl);
      setMenu(m);

      // keep selection valid
      if (m.categories.every((c) => c.id !== selectedCategoryId)) {
        setSelectedCategoryId(m.categories[0]?.id ?? null);
        setSelectedSubcategoryId(null);
      }
    } catch (e) {
      console.error("[MenuManager] load failed:", e);
      alert(e?.message || "Failed to load menu");
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever tenantServerUrl changes (e.g. after login / restaurant switch)
  useEffect(() => {
    if (!tenantServerUrl) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantServerUrl]);

  // ---- Toolbar actions ----
  const handleRefresh = load;

  const handleDownloadTemplate = (kind = "full") => {
    downloadMenuImportTemplate(kind);
  };

  const handleImport = async (file, kind = "full") => {
    if (!file) return;
    if (!tenantServerUrl) {
      alert("No tenant server URL. Sign in and try again.");
      return;
    }

    try {
      setLoading(true);
      const parsed = await parseMenuImportFile(file, { kind });
      const countForKind =
        kind === "categories"
          ? parsed.categories.length
          : kind === "subcategories"
          ? parsed.subcategories.length
          : kind === "items"
          ? parsed.items.length
          : kind === "sides"
          ? (parsed.sides || []).length
          : kind === "allergens"
          ? (parsed.allergens || []).length
          : parsed.categories.length +
            parsed.subcategories.length +
            parsed.items.length +
            (parsed.sides || []).length +
            (parsed.allergens || []).length;

      if (!countForKind) {
        const other = [
          parsed.categories.length && `${parsed.categories.length} categories`,
          parsed.subcategories.length &&
            `${parsed.subcategories.length} subcategories`,
          parsed.items.length && `${parsed.items.length} items`,
          (parsed.sides || []).length && `${parsed.sides.length} sides`,
          (parsed.allergens || []).length &&
            `${parsed.allergens.length} allergens`,
        ].filter(Boolean);
        throw new Error(
          other.length
            ? `This file has ${other.join(" and ")} but no ${kind} rows. Download the ${kind} template, or import from the matching column.`
            : "No data rows found. Download the blank template and fill in the header columns."
        );
      }

      const summary = await applyMenuImport({
        parsed,
        kind,
        tenantServerUrl,
        selectedCategory,
        selectedSubcategory,
        addMainCategory,
        addSubcategoryApi,
        createMenuItem,
        addSideMenuItem,
        addAllergen,
        fetchAllergens,
        username:
          localStorage.getItem("loggedUser_Username") ?? "admin",
      });
      await load();
      alert(formatImportSummary(summary));
    } catch (e) {
      alert(`Import failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!menu || !Array.isArray(menu.categories)) {
      console.error("No menu categories to export");
      return;
    }
    try {
      setLoading(true);
      const wb = await buildMenuWorkbook(menu, tenantServerUrl);
      XLSX.writeFile(wb, "menu.xlsx");
    } catch (e) {
      alert(`Export failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupAndClear = async () => {
    if (!tenantServerUrl) {
      alert("No tenant server URL. Sign in and try again.");
      return;
    }
    if (!menu?.categories?.length) {
      const okEmpty = window.confirm(
        "The menu looks empty in the app. Continue anyway?\nA SQL dump will still be taken from the server, then the menu tables will be cleared."
      );
      if (!okEmpty) return;
    }

    const ok1 = window.confirm(
      "Backup & Clear Menu\n\n" +
        "1) Download a ZIP with menu.xlsx + menu.sql\n" +
        "2) Then DELETE all categories, items, sides, allergens, and ratings from this restaurant.\n\n" +
        "This cannot be undone without restoring the backup. Continue?"
    );
    if (!ok1) return;

    const typed = window.prompt(
      'Type CLEAR (all caps) to confirm wiping the menu after the backup downloads:'
    );
    if (String(typed || "").trim() !== "CLEAR") {
      alert("Cancelled — confirmation text did not match.");
      return;
    }

    try {
      setLoading(true);
      const sqlPayload = await dumpMenuSQL(tenantServerUrl);
      await downloadMenuBackupZip({
        menu: menu || { categories: [] },
        tenantServerUrl,
        sqlPayload,
      });
      await clearMenu(tenantServerUrl, {
        confirm: "CLEAR",
        username: localStorage.getItem("loggedUser_Username") ?? "admin",
      });
      await load();
      setSelectedCategoryId(null);
      setSelectedSubcategoryId(null);
      setSelectedItemId(null);
      alert(
        "Backup downloaded and menu cleared.\nKeep the ZIP safe — you will need it to restore."
      );
    } catch (e) {
      console.error(e);
      alert(
        `Backup & Clear failed: ${e?.message || e}\n\n` +
          "If the ZIP downloaded but clear failed, your menu data is still on the server."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetRatings = async () => {
    if (!tenantServerUrl) {
      alert("No tenant server URL. Sign in and try again.");
      return;
    }

    const ok = window.confirm(
      "Reset all menu item ratings to 3?\n\n" +
        "This also clears stored customer rating history so averages start fresh from 3. Continue?"
    );
    if (!ok) return;

    const typed = window.prompt(
      "Type RESET (all caps) to confirm setting every menu item rating to 3:"
    );
    if (String(typed || "").trim() !== "RESET") {
      alert("Cancelled — confirmation text did not match.");
      return;
    }

    try {
      setLoading(true);
      const result = await resetMenuItemRatings(tenantServerUrl, {
        confirm: "RESET",
        username: localStorage.getItem("loggedUser_Username") ?? "admin",
      });
      await load();
      const total = result?.items_total ?? 0;
      const cleared = result?.ratings_cleared ?? 0;
      alert(
        `All menu item ratings are now 3.\n${total} item(s) updated. ${cleared} rating history row(s) cleared.`
      );
    } catch (e) {
      console.error(e);
      alert(`Reset ratings failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  // ---- Per-list exports ----
  const handleExportCategories = () => {
    if (!menu || !Array.isArray(menu.categories)) {
      alert("No categories to export");
      return;
    }

    // Drop nested subcategories so we don't get [object Object]
    const rows = sortedCategories.map(({ subcategories, ...rest }) => rest);
    downloadCsv(rows, "menu_categories.csv");
  };

  const handleExportSubcategories = () => {
    if (!selectedCategory) {
      alert("Select a category first to export its subcategories.");
      return;
    }

    const subcategories = sortedSubcategories;
    const rows = subcategories.map(({ items, ...subRest }) => ({
      category_id: selectedCategory.id,
      category_name: selectedCategory.name,
      category_code: selectedCategory.code,
      ...subRest, // id, name, subcode, visibility, etc.
    }));

    downloadCsv(
      rows,
      `menu_subcategories_${selectedCategory.code || selectedCategory.id}.csv`
    );
  };

  const handleExportItems = () => {
    if (!showingAllItems && (!selectedCategory || !selectedSubcategory)) {
      alert("Select a subcategory first to export its items, or turn on View all.");
      return;
    }

    const items = sortedDisplayedItems;
    const rows = items.map((item) => {
      const raw = { ...(item.raw || {}) };
      delete raw.allergens_resolved;
      delete raw.sides_resolved;
      return {
        category_id: item.categoryId ?? selectedCategory?.id,
        category_name: item.categoryName ?? selectedCategory?.name,
        category_code: item.categoryCode ?? selectedCategory?.code,

        subcategory_id: item.subcategoryId ?? selectedSubcategory?.id,
        subcategory_name: item.subcategoryName ?? selectedSubcategory?.name,
        subcategory_code: item.subcategoryCode ?? selectedSubcategory?.subcode,
        ...raw,
        allergens: formatAllergenTitlesForExport(item),
      };
    });

    const fileLabel = showingAllItems
      ? itemSearchQuery
        ? "search"
        : "all"
      : `${selectedCategory.code || selectedCategory.id}_${selectedSubcategory.subcode || selectedSubcategory.id}`;

    downloadCsv(rows, `menu_items_${fileLabel}.csv`);
  };


// ---------------------
// Helper: JSON → CSV
// ---------------------
// ---------------------
// CSV helpers
// ---------------------
function jsonToCsv(items) {
  if (!items || !items.length) return "";

  const headers = Object.keys(items[0]);
  const rows = items.map((obj) =>
    headers
      .map((h) => `"${(obj[h] ?? "").toString().replace(/"/g, '""')}"`)
      .join(",")
  );

  return [headers.join(","), ...rows].join("\n");
}

function downloadCsv(rows, filename) {
  if (!rows || !rows.length) {
    alert("Nothing to export");
    return;
  }

  const csv = jsonToCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}



  const handleAddCategory = () => setAddMainOpen(true);
  const handleAddSubcategory = () => {
    if (!selectedCategory) {
      alert("Select a main category first.");
      return;
    }
    setAddSubOpen(true);
  };

  // ---- Generic edit modal state ----
  const [editOpen, setEditOpen] = useState(false);
  const [editEntity, setEditEntity] = useState(null); // 'category' | 'subcategory' | 'item'
  const [editRecord, setEditRecord] = useState(null);
  const [excludeKeys, setExcludeKeys] = useState([]);

  const handleEditCategoryRow = (cat) => {
    setEditEntity("category");
    setEditRecord(cat);
    // setExcludeKeys([...])
    setEditOpen(true);
  };

  const handleEditSubcategoryRow = (sc) => {
    setEditEntity("subcategory");
    setEditRecord(sc);
    // setExcludeKeys([...])
    setEditOpen(true);
  };

  // ---- Item edit modal (specific) ----
  const [editingItem, setEditingItem] = useState(null);
  const [editingCode, setEditingCode] = useState(null);

  const currentCode = selectedCategory?.code ?? menu?.code ?? null;

  const locateItemInMenu = (item) => {
    if (!item) return;
    if (item.categoryId) setSelectedCategoryId(item.categoryId);
    if (item.subcategoryId) setSelectedSubcategoryId(item.subcategoryId);
    if (item.id) setSelectedItemId(item.id);
  };

  const handleOpenItemEdit = (item, passedCode) => {
    locateItemInMenu(item);
    setEditingItem(item);
    setEditingCode(
      passedCode ?? item?.categoryCode ?? item?.raw?.code ?? currentCode
    );
  };

  const handleItemSaved = async () => {
    await load();
    setEditingItem(null);
    setEditingCode(null);
  };

  const handleEditItemRow = (it) => {
    setEditEntity("item");
    setEditRecord(it);
    setExcludeKeys(["id", "code", "subcode"]);
    setEditOpen(true);
  };

  const handleDeleteCategory = async (cat) => {
    if (!cat || !tenantServerUrl) return;
    const ok = window.confirm(
      `Delete main category "${cat.name}"?\nThis may remove its subcategories and items.`
    );
    if (!ok) return;
    setLoading(true);
    try {
      await deleteMainCategory(tenantServerUrl, {
        id: cat.id,
        code: cat.code,
      });
      await load();
      if (selectedCategoryId === cat.id) {
        setSelectedCategoryId(menu.categories[0]?.id || null);
        setSelectedSubcategoryId(null);
      }
    } catch (e) {
      alert(e?.message || "Delete failed");
    } finally {
      setLoading(false);
      setEditOpen(false);
    }
  };

  const handleDeleteSubcategory = async (sc) => {
    if (!sc || !tenantServerUrl) return;
    const ok = window.confirm(`Delete subcategory "${sc.name}"?`);
    if (!ok) return;
    setLoading(true);
    try {
      await deleteSubcategoryApi(tenantServerUrl, {
        id: sc.id,
        code: selectedCategory?.code,
        subcode: sc.subcode,
      });
      await load();
      if (selectedSubcategoryId === sc.id) setSelectedSubcategoryId(null);
    } catch (e) {
      alert(e?.message || "Delete failed");
    } finally {
      setLoading(false);
      setEditOpen(false);
    }
  };

  const handleDeleteItem = async (it) => {
    if (!it || !tenantServerUrl) return;
    const ok = window.confirm(`Delete item "${it.name}"?`);
    if (!ok) return;
    setLoading(true);
    try {
      await deleteMenuItem(tenantServerUrl, { id: it.id });
      await handleItemSaved();
    } catch (e) {
      alert(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const submitEdit = async (patch) => {
    if (!tenantServerUrl) return;

    setLoading(true);
    const rec = editRecord || {};
    try {
      const bywho =
        localStorage.getItem("loggedUser_Username") ?? "admin";

      // Merge: patch should win over rec
      const base = { ...rec, ...patch, bywho };

      // Normalize visibility if present (preserve the edited value)
      if (base.visibility !== undefined) {
        const v =
          typeof base.visibility === "string"
            ? base.visibility.toLowerCase().trim()
            : base.visibility;
        base.visibility =
          v === 1 ||
          v === "1" ||
          v === true ||
          v === "true" ||
          v === "yes" ||
          v === "y"
            ? 1
            : 0;
      }

      if (editEntity === "category") {
        const payload = { ...base, subcategories: "main" };
        const result = await editCategoryFields(
          tenantServerUrl,
          payload
        );
        if (result === "success") {
          await load();
          setSelectedCategoryId(rec.id);
          setEditOpen(false);
          setEditRecord(null);
        }
      } else if (editEntity === "subcategory") {
        const payload = {
          main: base.main ?? selectedCategory?.name ?? base.category,
          code: base.code ?? selectedCategory?.code,
          ...base,
          items: "sub",
        };
        const result = await editSubcategoryFields(
          tenantServerUrl,
          payload
        );
        if (result === "success") {
          await load();
          setSelectedSubcategoryId(rec.id);
          setEditOpen(false);
          setEditRecord(null);
        }
      } else if (editEntity === "item") {
        const payload = {
          code: base.code ?? selectedCategory?.code,
          subcode: base.subcode ?? selectedSubcategory?.subcode,
          ...base,
        };
        await editItemFields(tenantServerUrl, payload);
        await handleItemSaved(); // already reloads & closes
      }
    } catch (e) {
      console.error(e);
      alert(`Saves failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const createMainCategory = async ({
    category,
    code,
    description,
    visibility,
  }) => {
    if (!tenantServerUrl) return;
    setLoading(true);
    try {
      const bywho =
        localStorage.getItem("loggedUser_Username") ?? "admin";
      const res = await addMainCategory(tenantServerUrl, {
        category,
        code,
        description,
        visibility,
        username: bywho,
      });
      if (res !== "success" && res !== "exists")
        throw new Error(String(res || "failed"));
      await load();
      // try to auto-select the new one
      const fresh = (m) =>
        m.categories.find((c) => c.code === code) || null;
      setSelectedCategoryId((prev) => fresh(menu)?.id || prev);
    } finally {
      setLoading(false);
    }
  };

  const createSubcategory = async ({
    main,
    code,
    category,
    subcode,
    description,
    visibility,
  }) => {
    if (!tenantServerUrl) return;
    setLoading(true);
    try {
      const bywho =
        localStorage.getItem("loggedUser_Username") ?? "admin";
      const res = await addSubcategoryApi(tenantServerUrl, {
        main,
        code,
        category,
        subcode,
        description,
        visibility,
        bywho,
      });
      if (res !== "success" && res !== "exists")
        throw new Error(String(res || "failed"));
      await load();
      // auto-select the newly added subcategory by subcode
      const fresh =
        menu.categories.find((c) => c.code === code) ||
        selectedCategory;
      const sub = fresh?.subcategories?.find(
        (s) => s.subcode === subcode
      );
      if (fresh?.id) setSelectedCategoryId(fresh.id);
      if (sub?.id) setSelectedSubcategoryId(sub.id);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!selectedCategory || !selectedSubcategory) {
      alert("Select a subcategory first.");
      return;
    }
    const blank = {
      name: "",
      price: 0,
      description: "",
      visibility: 1,
      rating: 3,
      code: selectedCategory.code,
      subcode: selectedSubcategory.subcode,
    };
    setEditingItem(blank);
    setEditingCode(selectedCategory.code);
  };

  const [isToolbarMinimized, setToolbarMinimized] = useState(false);
  const scrollThreshold = 100;

  const onScroll = useCallback(() => {
    const y = typeof window !== "undefined" ? window.scrollY ?? document.documentElement.scrollTop : 0;
    setToolbarMinimized(y > scrollThreshold);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  return (
    <div className="menu-manager-root">
      <div className={`menu-manager-toolbar ${isToolbarMinimized ? "menu-manager-toolbar--minimized" : ""}`}>
        <Toolbar
          onRefresh={handleRefresh}
          onImport={(file) => handleImport(file, "full")}
          onDownloadTemplate={() => handleDownloadTemplate("full")}
          onExport={handleExport}
          onBackupAndClear={handleBackupAndClear}
          onResetRatings={handleResetRatings}
          onAddCategory={handleAddCategory}
          onAddSubcategory={handleAddSubcategory}
          onAddItem={handleAddItem}
          onManageUniversalSides={() => setUniversalSidesOpen(true)}
          onManageAllergens={() => setAllergensOpen(true)}
        />
        <button
          type="button"
          className="menu-manager-toolbar-expand"
          aria-label="Expand menu actions"
          onClick={() => setToolbarMinimized(false)}
        >
          ▼ Show actions
        </button>
      </div>

      <div className={`menu-manager-content ${isToolbarMinimized ? "menu-manager-content--toolbar-minimized" : ""}`}>
      <div
        className="menu-manager-selected"
        style={{
          padding: 10,
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span>
          Selected:{" "}
          {showingAllItems
            ? itemSearchQuery
              ? "Search results"
              : "All items"
            : selectedCategory
            ? selectedCategory.name
            : "None"}
        </span>
        {loading && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontWeight: "normal",
              opacity: 0.8,
            }}
          >
            <Spinner />
            <span style={{ fontSize: 12, color: "#666" }}>
              Loading…
            </span>
          </span>
        )}
        {!tenantServerUrl && (
          <span style={{ marginLeft: 10, color: "#b30000" }}>
            (No tenant server URL)
          </span>
        )}
        <div className="menu-manager-item-tools">
          <input
            type="search"
            className="menu-manager-item-search"
            placeholder="Search menu items…"
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            aria-label="Search menu items"
          />
          <button
            type="button"
            className={`menu-manager-view-all-btn${viewAllItems ? " is-active" : ""}`}
            onClick={() => {
              setViewAllItems((v) => {
                const next = !v;
                setItemsExpanded(next);
                return next;
              });
            }}
            title="Show every menu item across all categories"
          >
            {viewAllItems ? "Viewing all" : "View all"}
          </button>
          {showingAllItems && (
            <button
              type="button"
              className={`menu-manager-view-all-btn${itemsExpanded ? " is-active" : ""}`}
              onClick={() => setItemsExpanded((v) => !v)}
              title={
                itemsExpanded
                  ? "Show categories and subcategories again"
                  : "Show All Items at full width"
              }
            >
              {itemsExpanded ? "Collapse" : "Expand"}
            </button>
          )}
        </div>
        <span className="menu-manager-totals" title="Totals across the whole menu">
          <span>
            {menuTotals.categories}{" "}
            {menuTotals.categories === 1 ? "category" : "categories"}
          </span>
          <span aria-hidden="true">·</span>
          <span>
            {menuTotals.subcategories}{" "}
            {menuTotals.subcategories === 1 ? "subcategory" : "subcategories"}
          </span>
          <span aria-hidden="true">·</span>
          <span>
            {menuTotals.items} {menuTotals.items === 1 ? "item" : "items"}
          </span>
        </span>
      </div>

      <div style={{ position: "relative" }} className="menu-manager-layout-wrap">
        {loading && <SpinnerOverlay />}

        <div
          className={`menu-manager-layout${itemsExpanded ? " is-items-expanded" : ""}`}
          style={{
            display: "flex",
            gap: 12,
            background: "#f1f1f1",
            padding: 12,
          }}
        >
          {!itemsExpanded && (
            <>
              <Column
                title="Categories"
                onDownloadTemplate={() => handleDownloadTemplate("categories")}
                onImport={(file) => handleImport(file, "categories")}
                onExport={handleExportCategories}
                sortValue={categorySort}
                sortOptions={CATEGORY_SORT_OPTIONS}
                onSortChange={setCategorySort}
              >
                <CategoryList
                  categories={sortedCategories}
                  selectedCategoryId={selectedCategoryId}
                  onSelect={(c) => {
                    setSelectedCategoryId(c.id);
                    setSelectedSubcategoryId(null);
                  }}
                  onEdit={handleEditCategoryRow}
                />
              </Column>

              <Column
                title="Subcategories"
                onDownloadTemplate={() => handleDownloadTemplate("subcategories")}
                onImport={(file) => handleImport(file, "subcategories")}
                onExport={handleExportSubcategories}
                sortValue={subcategorySort}
                sortOptions={SUBCATEGORY_SORT_OPTIONS}
                onSortChange={setSubcategorySort}
              >
                <SubcategoryList
                  subcategories={sortedSubcategories}
                  selectedSubcategoryId={selectedSubcategoryId}
                  onSelect={(sc) => setSelectedSubcategoryId(sc.id)}
                  onEdit={handleEditSubcategoryRow}
                />
              </Column>
            </>
          )}

          <Column
            title={
              itemSearchQuery
                ? "Search Results"
                : viewAllItems
                ? "All Items"
                : "Items"
            }
            rightBorder={false}
            onDownloadTemplate={() => handleDownloadTemplate("items")}
            onImport={(file) => handleImport(file, "items")}
            onExport={handleExportItems}
            sortValue={itemSort}
            sortOptions={ITEM_SORT_OPTIONS}
            onSortChange={setItemSort}
            expanded={itemsExpanded}
            onToggleExpand={
              showingAllItems ? () => setItemsExpanded((v) => !v) : undefined
            }
          >
            <div className="menu-manager-item-count">
              {itemSearchQuery
                ? `${sortedDisplayedItems.length} of ${allMenuItems.length} items`
                : showingAllItems
                ? `${sortedDisplayedItems.length} items`
                : selectedSubcategory
                ? `${sortedDisplayedItems.length} items`
                : "Select a subcategory, or view all"}
            </div>
            <ItemList
              code={currentCode}
              selectedItemId={selectedItemId}
              items={sortedDisplayedItems}
              showPath={showingAllItems}
              emptyMessage={
                itemSearchQuery
                  ? "No items match your search."
                  : showingAllItems
                  ? "No items on the menu."
                  : selectedSubcategory
                  ? "No items."
                  : "Select a subcategory, or click View all."
              }
              onSelect={(item) => {
                locateItemInMenu(item);
              }}
              onOpen={(item) => locateItemInMenu(item)}
              onEdit={(item) =>
                handleOpenItemEdit(item, item.categoryCode ?? currentCode)
              }
              onDelete={handleDeleteItem}
            />
          </Column>
        </div>
      </div>
      </div>

      {/* Generic edit modal */}
      <GenericEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        entity={editEntity || "item"}
        record={editRecord || {}}
        excludeKeys={excludeKeys}
        onSubmit={submitEdit}
        onAddItem={editEntity === "subcategory" ? handleAddItem : undefined}
        onDelete={
          editEntity === "category"
            ? () => handleDeleteCategory(editRecord)
            : editEntity === "subcategory"
            ? () => handleDeleteSubcategory(editRecord)
            : undefined
        }
      />

      {/* Item edit modal */}
      <EditMenuItemModal
        open={!!editingItem}
        item={editingItem}
        code={editingCode}
        parentPath={
          editingItem?.parentPath ||
          (selectedCategory && selectedSubcategory
            ? `${selectedCategory.name} > ${selectedSubcategory.name}`
            : "")
        }
        onClose={() => {
          setEditingItem(null);
          setEditingCode(null);
        }}
        onSaved={handleItemSaved}
      />

      {/* Add main category modal */}
      <AddMainCategoryModal
        open={addMainOpen}
        onClose={() => setAddMainOpen(false)}
        onCreate={createMainCategory}
      />

      <UniversalSidesModal
        open={universalSidesOpen}
        tenantServerUrl={tenantServerUrl}
        onClose={() => setUniversalSidesOpen(false)}
        onDownloadTemplate={() => handleDownloadTemplate("sides")}
        onImport={(file) => handleImport(file, "sides")}
      />

      <AllergenManagerModal
        open={allergensOpen}
        tenantServerUrl={tenantServerUrl}
        onClose={() => setAllergensOpen(false)}
        onDownloadTemplate={() => handleDownloadTemplate("allergens")}
        onImport={(file) => handleImport(file, "allergens")}
      />

      {/* Add subcategory modal */}
      <AddSubcategoryModal
        open={addSubOpen}
        onClose={() => setAddSubOpen(false)}
        onCreate={createSubcategory}
        parentCategory={selectedCategory}
        experienceOptions={EXPERIENCE_OPTIONS}
      />
    </div>
  );
};

export default MenuManager;
